import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, writeBatch, serverTimestamp, getDocs, query, orderBy,
} from '../lib/fsCompat';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { normalizeToCanonical } from '../utils/canonicalNormalizer';
import { annotateTx, contentHash as fpContentHash } from '../utils/txFingerprint';
import { registerTokenIcons } from '../utils/tokenIcons';
import { findMoonPayLedgerPairs, findSwapWalletInPairs, buildWalletEnrichment } from '../utils/moonpayLedgerDedupe';
import { findCsvApiTwins, mergeFieldsFromCsv } from '../utils/apiExchangeReconcile';
import { enrichTimestampsAndPatch } from '../utils/chainTimestampEnricher';

const LOCAL_KEY = 'red_txs';
const MIGRATION_KEY = 'red_migration_done';
const BACKFILL_KEY  = 'red_backfill_v1_done';

export function useTransactions() {
  const { user } = useAuth();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevUidRef = useRef(null);

  // Wenn der User von null auf einen eingeloggten User wechselt, muss loading
  // vor dem nächsten Browser-Paint auf true zurückgesetzt werden. Sonst gibt es
  // ein kurzes Render-Fenster (txsLoading=false, txs=[], user!=null) das
  // ConnectFirstSource oder andere Leer-Screens aufblitzen lässt, bevor
  // Firestore den ersten Snapshot geliefert hat.
  useLayoutEffect(() => {
    const uid = user?.uid ?? null;
    if (uid !== prevUidRef.current) {
      prevUidRef.current = uid;
      if (uid) setLoading(true);
    }
  }, [user]);

  const runMigration = useCallback(async (uid) => {
    if (localStorage.getItem(MIGRATION_KEY)) return;

    try {
      const colRef = collection(db, 'users', uid, 'transactions');
      const existing = await getDocs(colRef);

      if (!existing.empty) {
        // Firestore hat bereits Daten -> Migration ist obsolet. Den uralten
        // local-only Snapshot ENDGUELTIG loeschen, damit er nie wieder als
        // Re-Seed-Quelle nach einem Wipe dienen kann (Root-Cause des
        // wiederkehrenden 18M-SHIB-Geistbestands + Mobile/Web-Divergenz:
        // jedes Geraet hatte seinen eigenen veralteten red_txs-Snapshot).
        localStorage.setItem(MIGRATION_KEY, 'true');
        localStorage.removeItem(LOCAL_KEY);
        return;
      }

      const localData = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
      if (localData.length === 0) {
        localStorage.setItem(MIGRATION_KEY, 'true');
        return;
      }

      // Schreibe in Batches von max. 500 (Firestore-Limit)
      const BATCH_SIZE = 400;
      for (let i = 0; i < localData.length; i += BATCH_SIZE) {
        const chunk = localData.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(tx => {
          const ref = doc(colRef);
          batch.set(ref, {
            ...tx,
            id: String(tx.id),
            source: 'migrated_from_local',
            createdAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      localStorage.setItem(MIGRATION_KEY, 'true');
      // Snapshot nach erfolgreicher Migration konsumieren & loeschen — er hat
      // seinen Zweck erfuellt und darf nie wieder re-seeden.
      localStorage.removeItem(LOCAL_KEY);
    } catch (err) {
      // Migration schlägt still fehl — App bleibt funktionsfähig
      console.warn('Firestore-Migration fehlgeschlagen:', err);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setTxs([]);
      setLoading(false);
      return;
    }

    runMigration(user.uid);

    const colRef = collection(db, 'users', user.uid, 'transactions');
    const q = query(colRef, orderBy('datum', 'desc'));

    // Timeout-Fallback: wenn onSnapshot nach 5s (native) / 10s (web) weder Daten
    // noch Fehler liefert (z.B. Firestore-Verbindung hängt), loading aufheben.
    const _native = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
    const loadingTimeout = setTimeout(() => {
      console.warn('[useTransactions] Firestore-Listener Timeout');
      setLoading(false);
    }, _native ? 5000 : 10000);

    const unsub = onSnapshot(
      q,
      (snap) => {
        clearTimeout(loadingTimeout);
        const loaded = snap.docs.map(d => normalizeToCanonical({ ...d.data(), _docId: d.id }));
        registerTokenIcons(loaded); // On-Chain-SPL-Icons (coinIcon*) → Symbol-Map für CoinImage
        setTxs(loaded);
        setLoading(false);
      },
      (err) => {
        clearTimeout(loadingTimeout);
        console.error('Firestore-Listener Fehler:', err);
        setLoading(false);
      }
    );

    return () => { clearTimeout(loadingTimeout); unsub(); };
  }, [user, runMigration]);

  const addTx = useCallback(async (tx) => {
    if (!user) return null;
    const annotated = annotateTx(tx, { source: tx.tsSource || 'manual' });
    const colRef = collection(db, 'users', user.uid, 'transactions');
    const ref = await addDoc(colRef, {
      ...annotated,
      id: String(annotated.id || Date.now()),
      createdAt: serverTimestamp(),
      source: annotated.source || 'manual',
    });
    return ref.id;
  }, [user]);

  const updateTx = useCallback(async (tx) => {
    if (!user || !tx._docId) return;
    const { _docId, ...data } = tx;
    const ref = doc(db, 'users', user.uid, 'transactions', _docId);
    await updateDoc(ref, data);
  }, [user]);

  const patchTx = useCallback(async (docId, fields) => {
    if (!user || !docId) return;
    const ref = doc(db, 'users', user.uid, 'transactions', docId);
    await updateDoc(ref, fields);
  }, [user]);

  const deleteTx = useCallback(async (tx) => {
    if (!user || !tx._docId) return;
    // Partner-Link vorher räumen, damit kein Dangling-Verweis zurückbleibt.
    // Der Partner würde sonst als "bereits verbunden" blockiert sein und nie
    // wieder automatisch matchen können.
    if (tx.linkedTxId) {
      const partner = txs.find(t => t._docId === tx.linkedTxId);
      if (partner?._docId) {
        try {
          const partnerRef = doc(db, 'users', user.uid, 'transactions', partner._docId);
          await updateDoc(partnerRef, {
            linkedTxId: null, manualLink: null, transferGroupId: null,
          });
        } catch (err) {
          console.warn('Partner-Link-Cleanup beim Löschen fehlgeschlagen:', err);
        }
      }
    }
    const ref = doc(db, 'users', user.uid, 'transactions', tx._docId);
    await deleteDoc(ref);
  }, [user, txs]);

  const addBatch = useCallback(async (txArray, options = {}) => {
    if (!user || txArray.length === 0) return { added: 0, dupes: 0 };

    // Race-Condition-Schutz: Wenn der Caller vor addBatch TXs gelöscht hat
    // (Replace-Flow in App.handleImportCsv), ist der `txs`-Closure noch nicht
    // re-rendert — er enthält die gerade gelöschten TXs. Ohne excludeIds
    // würde Dedup die neuen TXs gegen die gelöschten Alten matchen und sie
    // fälschlich als „bereits da" verwerfen — Folge: 0 neue, 173 weg.
    const excludeIds = options.excludeIds
      ? new Set(options.excludeIds.map(String))
      : null;
    const liveTxs = excludeIds
      ? txs.filter(t => !excludeIds.has(t._docId || String(t.id)))
      : txs;

    // ── Cross-Import-Deduplication ────────────────────────────────────────────
    // Tier 0: externalId (z.B. "bitpanda:trade:uuid") — deterministischer
    //   Primärschlüssel von Exchange-APIs. Wenn vorhanden, ist das die
    //   verlässlichste Quelle: derselbe Trade auf Bitpanda hat IMMER dieselbe
    //   trade-uuid, egal ob der User Felder editiert oder mehrmals syncht.
    // Tier 1: Blockchain-TX-Hash (sicherstes Signal für on-chain TXs).
    // Tier 2: contentHash aus txFingerprint — quantisiert nur so genau wie die
    //   Quelle (sekundengenau vs. tagesgenau).
    // Tier-1-Key: hash + typ. Der Solana-TX-Hash ist auf BEIDEN Seiten eines
    // Transfers identisch (Phantom-AUSGANG hat dieselbe Signature wie der
    // korrespondierende Ledger-EINGANG). Nur hash zu vergleichen wuerde den
    // AUSGANG als Duplikat des EINGANG verwerfen. typ unterscheidet die Seiten.
    const chainKey = (t) => t.hash ? `${t.hash}|${t.typ || ''}` : null;
    const existingExternalIds = new Set(liveTxs.map(t => t.externalId).filter(Boolean));
    const existingChainHashes = new Set(liveTxs.map(chainKey).filter(Boolean));
    const existingContentHash = new Set(
      liveTxs.map(t => t.contentHash || fpContentHash(t)).filter(Boolean)
    );

    const toAdd = [];
    let dupeCount = 0;

    for (const raw of txArray) {
      const tx = annotateTx(raw, { source: raw.tsSource || 'csv' });

      const ck = chainKey(tx);

      // Falls der Caller (Review-Screen) explizit "Trotzdem importieren" gesetzt hat:
      if (tx._forceImport) {
        const { _forceImport, _collisionGroup, _collisionSize, ...clean } = tx;
        toAdd.push(clean);
        if (tx.externalId) existingExternalIds.add(tx.externalId);
        if (ck) existingChainHashes.add(ck);
        if (tx.contentHash) existingContentHash.add(tx.contentHash);
        continue;
      }

      // Tier 0: externalId
      if (tx.externalId && existingExternalIds.has(tx.externalId)) { dupeCount++; continue; }
      // Tier 1: Chain-Hash + typ (zwei Seiten desselben Transfers haben dieselbe Signature)
      if (ck && existingChainHashes.has(ck)) { dupeCount++; continue; }
      // Tier 2: contentHash
      if (tx.contentHash && existingContentHash.has(tx.contentHash)) { dupeCount++; continue; }

      const { _collisionGroup, _collisionSize, ...clean } = tx;
      toAdd.push(clean);
      if (tx.externalId) existingExternalIds.add(tx.externalId);
      if (ck) existingChainHashes.add(ck);
      if (tx.contentHash) existingContentHash.add(tx.contentHash);
    }

    const colRef = collection(db, 'users', user.uid, 'transactions');
    const BATCH_SIZE = 400;

    // ── Cross-Source-Dedupe + Wallet-Enrichment (vor dem Write) ──────────────
    // MoonPay-Kauf + zugehöriger Ledger-Eingang sind dieselbe Echtwelt-TX.
    // Wir behalten den MoonPay-Kauf (hat Anschaffungskosten) und reichern ihn um
    // Wallet-Felder aus dem Ledger-Eintrag an (wallet, walletHash).
    // Der Ledger-Eintrag wird verworfen — er wäre ein FIFO-Doppelinflow.
    let enriched = 0;
    let scrubbed = 0;
    const existingMoonpayPatches = []; // [{docId, fields}] — für nach dem Write
    const existingLedgerToDelete = []; // docIds bestehender Ledger-Einträge
    try {
      const pairs = findMoonPayLedgerPairs([...liveTxs, ...toAdd]);
      if (pairs.length > 0) {
        const toAddSet = new Set(toAdd);
        const ledgerInToAdd = new Set();
        // Map: identity-of-moonpay-in-toAdd → enrichment-fields
        const enrichToAdd = new Map();

        for (const { ledger, moonpay } of pairs) {
          const enrichment = buildWalletEnrichment(ledger);

          if (toAddSet.has(ledger)) ledgerInToAdd.add(ledger);

          if (toAddSet.has(moonpay)) {
            // MoonPay-Kauf ist neu → anreichern bevor er geschrieben wird
            enrichToAdd.set(moonpay, enrichment);
          } else if (moonpay._docId) {
            // MoonPay-Kauf ist schon in Firestore → später patchen. ABER: ein vom User
            // manuell gesetztes Wallet-Label NIE überschreiben (Punkt 2: neues Hochladen
            // darf bestehende, vom User bearbeitete Daten nicht überschreiben). walletHash
            // (Provenienz) bleibt erlaubt; auto-gesetzte Labels dürfen korrigiert werden.
            const fields = { ...enrichment };
            if (moonpay.userEdited && moonpay.wallet) delete fields.wallet;
            if (Object.keys(fields).length > 0) {
              existingMoonpayPatches.push({ docId: moonpay._docId, fields });
            }
          }

          // Ledger-Eintrag, der bereits in Firestore liegt → später löschen
          if (!toAddSet.has(ledger) && ledger._docId) {
            existingLedgerToDelete.push(ledger._docId);
          }
        }

        // toAdd reduzieren + anreichern in einem Rutsch
        if (ledgerInToAdd.size > 0 || enrichToAdd.size > 0) {
          const filteredToAdd = [];
          for (const tx of toAdd) {
            if (ledgerInToAdd.has(tx)) { scrubbed++; continue; }
            const e = enrichToAdd.get(tx);
            if (e) { enriched++; filteredToAdd.push({ ...tx, ...e }); }
            else filteredToAdd.push(tx);
          }
          toAdd.length = 0;
          toAdd.push(...filteredToAdd);
        }
      }
    } catch (err) {
      console.warn('[addBatch] Cross-Source-Dedupe (pre-write) fehlgeschlagen:', err);
    }

    // ── Swap-Settlement-Dedupe: On-Chain-Wallet-Eingang, der den Output eines
    // Wallet-Swaps doppelt (z.B. SOL→ONDO im Ledger + späterer ETH-Adress-Import).
    // Der Swap hält bereits Coin/Menge/Kostenbasis → der NEUE Wallet-Eingang ist redundant.
    // ⚠️ SICHERHEIT: Diese Dedupe löscht NUR neue Import-Zeilen (toAdd), NIE bestehende
    // Portfolio-TX. Sonst kann ein unbeteiligter Import rückwirkend echten Bestand löschen
    // (vgl. FLR-Regression: ein Wallet-Adress-Import nuked die bestehende Ledger-Einzahlung).
    // Der Transfer-Schutz in findSwapWalletInPairs greift zusätzlich.
    try {
      const swapPairs = findSwapWalletInPairs([...liveTxs, ...toAdd]);
      if (swapPairs.length > 0) {
        const toAddSet = new Set(toAdd);
        const dropFromToAdd = new Set();
        for (const { walletIn } of swapPairs) {
          if (toAddSet.has(walletIn)) dropFromToAdd.add(walletIn); // nur NEU, nie bestehend
        }
        if (dropFromToAdd.size > 0) {
          const filtered = toAdd.filter(tx => !dropFromToAdd.has(tx));
          scrubbed += toAdd.length - filtered.length;
          toAdd.length = 0;
          toAdd.push(...filtered);
        }
      }
    } catch (err) {
      console.warn('[addBatch] Swap-Settlement-Dedupe fehlgeschlagen:', err);
    }

    // ── CSV ↔ Exchange-API Reconcile (börsenUNabhängig) ─────────────────────
    // Lädt ein Nutzer zuerst CSV-Exports einer Börse hoch und verbindet später
    // die API derselben Börse, existiert jede Buchung doppelt (csv_import +
    // exchange_api). Der contentHash-Dedup (Tier 2) fängt das nicht zuverlässig:
    // CSV trägt oft Lokalzeit ohne Offset → tsBucket divergiert von der UTC-API,
    // im Extremfall sogar das Datum (Folgetag). Lösung: die API ist kanonisch.
    // Existiert für eine CSV-Zeile ein exakter API-Zwilling, weicht die CSV-Zeile,
    // überträgt aber vorher ihre Transfer-Verbindungen + Nutzer-Edits auf die
    // API-Zeile, und die Transfer-Gegenseite wird vom CSV-_docId auf das API-
    // _docId umgehängt (sonst verwaist sie → Phantombestand, vgl.
    // fix_stale_replace_data_loss). Deckt alle Reihenfolgen ab: CSV→API, API→CSV,
    // beide neu, und bestehende Altlast (existing↔existing).
    let toAddRefs = null;
    const reconcileCsvDocsToDelete = []; // bestehende CSV-_docIds zum Löschen
    const reconcileApiPatches = [];      // [{docId, fields}] bestehende API-Zeilen anreichern
    const reconcilePartnerRepoints = []; // [{docId, fields:{linkedTxId}}] Gegenseite umhängen
    try {
      const twins = findCsvApiTwins([...liveTxs, ...toAdd]);
      if (twins.length > 0) {
        const toAddSet = new Set(toAdd);
        const dropCsv = new Set();
        const csvExistingTwins = []; // [{csvDocId, api}] — apiDocId erst nach Ref-Vergabe
        for (const { csv, api } of twins) {
          const patch = mergeFieldsFromCsv(api, csv);
          if (toAddSet.has(api)) {
            // API-Zwilling ist neu → Patch direkt ins zu schreibende Objekt mergen
            if (Object.keys(patch).length) Object.assign(api, patch);
          } else if (api._docId && Object.keys(patch).length) {
            reconcileApiPatches.push({ docId: api._docId, fields: patch });
          }
          if (toAddSet.has(csv)) {
            dropCsv.add(csv); // CSV-Zwilling ist neu → gar nicht erst schreiben
          } else if (csv._docId) {
            reconcileCsvDocsToDelete.push(csv._docId);
            csvExistingTwins.push({ csvDocId: csv._docId, api });
          }
        }
        if (dropCsv.size > 0) {
          const filtered = toAdd.filter(tx => !dropCsv.has(tx));
          scrubbed += toAdd.length - filtered.length;
          toAdd.length = 0;
          toAdd.push(...filtered);
        }
        // Stabile docIds für alle neuen Zeilen vergeben, damit ein in toAdd
        // liegender API-Zwilling fürs Partner-Umhängen referenzierbar ist.
        toAddRefs = toAdd.map(() => doc(colRef));
        const refByTx = new Map();
        toAdd.forEach((tx, i) => refByTx.set(tx, toAddRefs[i]));
        const deleteSet = new Set(reconcileCsvDocsToDelete);
        for (const { csvDocId, api } of csvExistingTwins) {
          const apiDocId = api._docId || refByTx.get(api)?.id;
          if (!apiDocId) continue;
          for (const p of liveTxs) {
            if (!p._docId || p._docId === csvDocId) continue;
            if (deleteSet.has(p._docId)) continue; // Partner wird selbst entfernt
            if (String(p.linkedTxId) === String(csvDocId)) {
              reconcilePartnerRepoints.push({ docId: p._docId, fields: { linkedTxId: apiDocId } });
            }
          }
        }
      }
    } catch (err) {
      console.warn('[addBatch] CSV↔API-Reconcile (pre-write) fehlgeschlagen:', err);
    }

    if (!toAddRefs) toAddRefs = toAdd.map(() => doc(colRef));
    for (let i = 0; i < toAdd.length; i += BATCH_SIZE) {
      const chunk = toAdd.slice(i, i + BATCH_SIZE);
      const refChunk = toAddRefs.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      chunk.forEach((tx, j) => {
        batch.set(refChunk[j], {
          ...tx,
          id: String(tx.id || Date.now()),
          createdAt: serverTimestamp(),
          source: tx.source || 'csv_import',
        });
      });
      await batch.commit();
    }

    // ── Nach dem Write: bestehende MoonPay-Kaufs patchen + bestehende Ledger-Einträge löschen
    try {
      if (existingMoonpayPatches.length > 0) {
        for (let i = 0; i < existingMoonpayPatches.length; i += BATCH_SIZE) {
          const chunk = existingMoonpayPatches.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          chunk.forEach(({ docId, fields }) => batch.update(doc(colRef, docId), fields));
          await batch.commit();
        }
        enriched += existingMoonpayPatches.length;
      }
      if (existingLedgerToDelete.length > 0) {
        for (let i = 0; i < existingLedgerToDelete.length; i += BATCH_SIZE) {
          const chunk = existingLedgerToDelete.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          chunk.forEach(docId => batch.delete(doc(colRef, docId)));
          await batch.commit();
        }
        scrubbed += existingLedgerToDelete.length;
      }
      if (scrubbed > 0 || enriched > 0) {
        console.log(`[addBatch] MoonPay↔Ledger: ${enriched} Kaufs angereichert, ${scrubbed} Ledger-Einträge verworfen`);
      }
    } catch (err) {
      console.warn('[addBatch] Cross-Source-Dedupe (post-write) fehlgeschlagen:', err);
    }

    // ── Nach dem Write: CSV↔API-Reconcile anwenden (API-Anreicherung + Partner-
    //    Umhängen + CSV-Zwillinge löschen). Reihenfolge: erst Updates, dann Delete. ──
    try {
      const reconcileUpdates = [...reconcileApiPatches, ...reconcilePartnerRepoints];
      for (let i = 0; i < reconcileUpdates.length; i += BATCH_SIZE) {
        const chunk = reconcileUpdates.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(({ docId, fields }) => batch.update(doc(colRef, docId), fields));
        await batch.commit();
      }
      if (reconcileCsvDocsToDelete.length > 0) {
        for (let i = 0; i < reconcileCsvDocsToDelete.length; i += BATCH_SIZE) {
          const chunk = reconcileCsvDocsToDelete.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          chunk.forEach(docId => batch.delete(doc(colRef, docId)));
          await batch.commit();
        }
        scrubbed += reconcileCsvDocsToDelete.length;
      }
      if (reconcileCsvDocsToDelete.length || reconcileApiPatches.length || reconcilePartnerRepoints.length) {
        console.log(`[addBatch] CSV↔API: ${reconcileCsvDocsToDelete.length} CSV-Zwillinge durch API ersetzt, ${reconcileApiPatches.length} API-Zeilen angereichert, ${reconcilePartnerRepoints.length} Transfer-Gegenseiten umgehängt`);
      }
    } catch (err) {
      console.warn('[addBatch] CSV↔API-Reconcile (post-write) fehlgeschlagen:', err);
    }

    return { added: toAdd.length, dupes: dupeCount, scrubbed, enriched };
  }, [user, txs]);

  /** Berechnet contentHash + ts für bestehende TX die noch keinen haben.
   *  Läuft einmal pro Browser (Flag in localStorage). Stille Operation. */
  const runBackfill = useCallback(async (uid, currentTxs) => {
    if (localStorage.getItem(BACKFILL_KEY)) return;
    if (!currentTxs || currentTxs.length === 0) {
      localStorage.setItem(BACKFILL_KEY, 'true');
      return;
    }
    const missing = currentTxs.filter(t => !t.contentHash || !t.ts);
    if (missing.length === 0) {
      localStorage.setItem(BACKFILL_KEY, 'true');
      return;
    }
    try {
      const BATCH_SIZE = 400;
      for (let i = 0; i < missing.length; i += BATCH_SIZE) {
        const chunk = missing.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(tx => {
          if (!tx._docId) return;
          const ann = annotateTx(tx, { source: tx.tsSource || tx.source || 'migrated_from_local' });
          const ref = doc(db, 'users', uid, 'transactions', tx._docId);
          const patch = {};
          if (!tx.ts && ann.ts) patch.ts = ann.ts;
          if (!tx.tsPrecision && ann.tsPrecision) patch.tsPrecision = ann.tsPrecision;
          if (!tx.tsSource && ann.tsSource) patch.tsSource = ann.tsSource;
          if (!tx.contentHash && ann.contentHash) patch.contentHash = ann.contentHash;
          if (Object.keys(patch).length > 0) batch.update(ref, patch);
        });
        await batch.commit();
      }
      localStorage.setItem(BACKFILL_KEY, 'true');
    } catch (err) {
      console.warn('Backfill (ts/contentHash) fehlgeschlagen:', err);
    }
  }, []);

  useEffect(() => {
    if (!user || loading) return;
    runBackfill(user.uid, txs);
  }, [user, loading, txs, runBackfill]);

  // Block-Zeitstempel-Backfill: holt Sekundenpraezision per Blockchain-Lookup
  // fuer alle bestehenden TXs mit hash, die noch tagesgenau sind. Laeuft einmal
  // pro Browser-Session, silent, sequentiell als Patches (kein Batch noetig —
  // typische Volumina sind < 100 TXs).
  const runChainTimestampBackfill = useCallback(async (uid, currentTxs) => {
    if (!currentTxs || currentTxs.length === 0) return;
    const candidates = currentTxs.filter(t => t._docId && t.hash && t.tsPrecision !== 'second');
    if (candidates.length === 0) return;
    try {
      await enrichTimestampsAndPatch(candidates, async (docId, patch) => {
        const ref = doc(db, 'users', uid, 'transactions', docId);
        await updateDoc(ref, patch);
      });
    } catch (err) {
      console.warn('Chain-Timestamp-Backfill fehlgeschlagen:', err);
    }
  }, []);

  // In-Memory-Flag (eine Session-Laeuferin pro Tab — onSnapshot triggert sonst
  // bei jedem Firestore-Update den Backfill neu).
  const chainBackfillRanRef = useRef(false);
  useEffect(() => {
    if (!user || loading) return;
    if (chainBackfillRanRef.current) return;
    chainBackfillRanRef.current = true;
    runChainTimestampBackfill(user.uid, txs);
  }, [user, loading, txs, runChainTimestampBackfill]);

  // Dangling-Link-Cleanup: nach CSV-Reimport (oder TX-Löschungen ohne
  // Partner-Cleanup in alten App-Versionen) zeigen `linkedTxId`-Felder evtl.
  // auf Docs, die nicht mehr existieren. Solche Verweise blockieren den
  // Auto-Matcher (Partner gilt als "bereits verbunden") und müssen geräumt
  // werden, damit Transfer-Paare wieder frisch zusammenfinden.
  const danglingCleanupRanRef = useRef(false);
  useEffect(() => {
    if (!user || loading) return;
    if (danglingCleanupRanRef.current) return;
    if (txs.length === 0) return;
    danglingCleanupRanRef.current = true;

    const docIds = new Set(txs.map(t => t._docId).filter(Boolean));
    const dangling = txs.filter(t =>
      t._docId && t.linkedTxId && !docIds.has(t.linkedTxId)
    );
    if (dangling.length === 0) return;

    (async () => {
      try {
        const BATCH_SIZE = 400;
        for (let i = 0; i < dangling.length; i += BATCH_SIZE) {
          const chunk = dangling.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          chunk.forEach(tx => {
            const ref = doc(db, 'users', user.uid, 'transactions', tx._docId);
            batch.update(ref, {
              linkedTxId: null, manualLink: null, transferGroupId: null,
            });
          });
          await batch.commit();
        }
        console.log(`[useTransactions] ${dangling.length} Dangling-Transfer-Links geräumt`);
      } catch (err) {
        console.warn('Dangling-Link-Cleanup fehlgeschlagen:', err);
        danglingCleanupRanRef.current = false;
      }
    })();
  }, [user, loading, txs]);

  const deleteBulkTxs = useCallback(async (txArray) => {
    if (!user || txArray.length === 0) return;
    const colRef = collection(db, 'users', user.uid, 'transactions');
    const CHUNK = 400;
    for (let i = 0; i < txArray.length; i += CHUNK) {
      const batch = writeBatch(db);
      txArray.slice(i, i + CHUNK).forEach(tx => {
        if (tx._docId) batch.delete(doc(colRef, tx._docId));
      });
      await batch.commit();
    }
  }, [user]);

  const deleteAllTxs = useCallback(async () => {
    if (!user) return;
    const colRef = collection(db, 'users', user.uid, 'transactions');
    const snap = await getDocs(colRef);
    const BATCH_SIZE = 400;
    for (let i = 0; i < snap.docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      snap.docs.slice(i, i + BATCH_SIZE).forEach(d => batch.delete(doc(colRef, d.id)));
      await batch.commit();
    }
    // KRITISCH: NICHT MIGRATION_KEY entfernen!
    //
    // Frueher stand hier `localStorage.removeItem(MIGRATION_KEY)`. Das war ein
    // fataler Bug: runMigration() laeuft, wenn MIGRATION_KEY fehlt. Nach dem
    // Wipe ist Firestore leer -> runMigration sieht "leer + alte red_txs im
    // localStorage" -> re-importiert den UR-ALTEN local-only Snapshot (vor
    // Firebase, geschrieben vom kaputten v0-Parser mit dem 8090.612-SHIB-Bug).
    // Symptom: nach JEDEM "Alle loeschen" + Re-Import standen sofort wieder
    // exakt 18.072.521,938 SHIB im Bestand, und frische CSVs kamen obendrauf
    // (doppelte/dreifache Bestaende).
    //
    // Korrekt: Migration als ERLEDIGT markieren (verhindert Re-Seed) UND den
    // veralteten local-only Snapshot endgueltig loeschen. Nach einem expliziten
    // Wipe will der User garantiert KEINE Wiederkehr alter local-Daten.
    localStorage.setItem(MIGRATION_KEY, 'true');
    localStorage.removeItem(LOCAL_KEY);
    localStorage.removeItem(BACKFILL_KEY);

    // Cursor aller Exchange-Connections nullen — sonst skipped der Sync alles,
    // weil er die gerade gelöschten TXs noch für "bekannt" hält.
    try {
      const connRef = collection(db, 'users', user.uid, 'exchangeConnections');
      const connSnap = await getDocs(connRef);
      if (!connSnap.empty) {
        const batch = writeBatch(db);
        connSnap.docs.forEach(d => batch.update(doc(connRef, d.id), {
          cursor: null,
          lastSyncAt: null,
          lastSyncStatus: 'idle',
          lastSyncError: null,
        }));
        await batch.commit();
      }
    } catch (err) {
      console.warn('Connection-Cursor-Reset fehlgeschlagen:', err);
    }
  }, [user]);

  return { txs, loading, addTx, updateTx, patchTx, deleteTx, deleteBulkTxs, addBatch, deleteAllTxs };
}
